import { type ConditionalOperator, findConditionRuleProblem, pickT } from "@repo/shared";
import type {
  CreateFlowPageRequest,
  CustomFieldOption,
  FlowField,
  GlobalPosition,
  GoalPosition,
  UpdateFlowPageRequest,
} from "@repo/shared/types";
import { useEffect, useState } from "react";
import { conditionRuleProblemMessage } from "@/components/conditional-display/condition-rule-problem-message";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ApiError, patch, post } from "@/lib/api/_client";
import { useFlowConfiguredAssessmentTypes } from "@/lib/hooks/use-configured-assessment-types";
import { useFlowQuizzes } from "@/lib/hooks/use-quizzes";
import type { FlowPageWithComponents, PageType, PageVisibility } from "@/lib/types/flow";
import { ConditionalVisibilityFields } from "./conditional-visibility-fields";
import { T } from "./flow-page-dialog.i18n";

type ClientCustomField = {
  id: string;
  name: string;
  type: string;
  options?: CustomFieldOption[] | null;
  isActive: boolean;
};

/** Where a new page lands — fixed by the section whose Add Page button was pressed. */
export type FlowPageCreateConfig = {
  visibility: PageVisibility;
  pageType: PageType;
  globalPosition: GlobalPosition;
  /** QL-117 — goal pages only: before or after the goal's portfolio pages. */
  goalPosition: GoalPosition;
};

type FlowPageDialogProps = {
  flowId: string;
  flowLanguage: string;
  page: FlowPageWithComponents | null;
  availableFields: FlowField[];
  clientCustomFields?: ClientCustomField[];
  open: boolean;
  onClose: (success: boolean) => void;
  createConfig?: FlowPageCreateConfig | null; // Configuration for creating new pages
  /** QL-120 — enables the goal-type source on goal/portfolio pages. */
  portfolioCollectionId?: string | null;
};

export function FlowPageDialog({
  flowId,
  flowLanguage,
  page,
  availableFields,
  clientCustomFields = [],
  open,
  onClose,
  createConfig,
  portfolioCollectionId = null,
}: FlowPageDialogProps) {
  const t = pickT(T, flowLanguage);

  // QL-13 — the flow's quizzes, so a rule naming a deleted one is caught on
  // save rather than silently hiding content at runtime. While the list is
  // loading it is empty, which would read as "every quiz is gone", so the
  // check waits for it.
  const { quizzes: flowQuizzes, isUnknown: quizzesUnknown } = useFlowQuizzes(flowId);
  const { configuredAssessmentTypes, isUnknown: assessmentTypesUnknown } =
    useFlowConfiguredAssessmentTypes(flowId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState("");
  const [enableAdvisorNotes, setEnableAdvisorNotes] = useState(false);
  const [requireAdvisorNotes, setRequireAdvisorNotes] = useState(false);

  // Conditional visibility state
  const [conditionalEnabled, setConditionalEnabled] = useState(false);
  const [sourceFieldId, setSourceFieldId] = useState("");
  const [operator, setOperator] = useState<ConditionalOperator>("equals");
  const [conditionValue, setConditionValue] = useState("");

  // Determine if editing (has page) or creating (has createConfig)
  const isEditing = !!page;
  const isCreating = !page && !!createConfig;

  // Derive pageType and visibility for conditional visibility fields
  const pageType: PageType = page?.pageType || createConfig?.pageType || "global";
  const visibility: PageVisibility = page?.visibility || createConfig?.visibility || "internal";

  useEffect(() => {
    if (open) {
      if (page) {
        // Editing existing page
        setName(page.name);
        setEnableAdvisorNotes(page.enableAdvisorNotes || false);
        setRequireAdvisorNotes(page.requireAdvisorNotes || false);

        // Load conditional visibility
        const conditional = page.conditionalVisibility as any;
        if (conditional) {
          setConditionalEnabled(conditional.enabled || false);
          setSourceFieldId(conditional.sourceFieldId || "");
          setOperator(conditional.operator || "equals");
          setConditionValue(conditional.value || "");
        } else {
          setConditionalEnabled(false);
          setSourceFieldId("");
          setOperator("equals");
          setConditionValue("");
        }
      } else {
        // Creating new page
        setName("");
        setEnableAdvisorNotes(false);
        setRequireAdvisorNotes(false);
        setConditionalEnabled(false);
        setSourceFieldId("");
        setOperator("equals");
        setConditionValue("");
      }
      setError(null);
      setLoading(false);
    }
  }, [page, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t.pageNameRequired);
      return;
    }

    const ruleProblem = conditionalEnabled
      ? findConditionRuleProblem(
          { sourceFieldId, operator, value: conditionValue },
          {
            quizzes: quizzesUnknown ? null : flowQuizzes,
            assessmentTypes: assessmentTypesUnknown ? null : configuredAssessmentTypes,
          },
        )
      : null;
    if (ruleProblem) {
      setError(conditionRuleProblemMessage(ruleProblem, flowLanguage));
      return;
    }

    setLoading(true);

    try {
      // Build conditional visibility config
      const conditionalVisibility = conditionalEnabled
        ? {
            enabled: true,
            sourceFieldId,
            operator,
            value: conditionValue,
          }
        : null;

      if (isEditing && page) {
        // Update existing page
        await patch(`/internal/flows/${flowId}/pages/${page.id}`, {
          name: name.trim(),
          conditionalVisibility,
          enableAdvisorNotes,
          requireAdvisorNotes: enableAdvisorNotes && requireAdvisorNotes,
        } satisfies UpdateFlowPageRequest);
      } else if (isCreating && createConfig) {
        // Create new page with pre-configured options
        const created = await post<{ id: string }>(`/internal/flows/${flowId}/pages`, {
          name: name.trim(),
          pageType: createConfig.pageType,
          visibility: createConfig.visibility,
          globalPosition: createConfig.globalPosition,
          goalPosition: createConfig.goalPosition,
        } satisfies CreateFlowPageRequest);

        // If we have conditional visibility or advisor notes, update the newly created page
        if (created.id && (conditionalVisibility || enableAdvisorNotes)) {
          await patch(`/internal/flows/${flowId}/pages/${created.id}`, {
            conditionalVisibility,
            enableAdvisorNotes,
            requireAdvisorNotes: enableAdvisorNotes && requireAdvisorNotes,
          } satisfies UpdateFlowPageRequest);
        }
      }

      onClose(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.failedToSavePage);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose(false)}>
      <DialogScrollContent className="max-w-2xl">
        <DialogScrollHeader>
          <DialogTitle>{isEditing ? t.editPage : t.createNewPage}</DialogTitle>
          <DialogDescription>
            {isEditing ? t.editPageDescription : t.createPageDescription}
          </DialogDescription>
        </DialogScrollHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
          <DialogBody className="space-y-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">
                {t.pageName} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.pageNamePlaceholder}
                disabled={loading}
                autoFocus
              />
            </div>

            {/* Advisor Notes - only for internal pages */}
            {visibility === "internal" && (
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="advisor-notes" className="text-base">
                    {t.enableAdvisorNotes}
                  </Label>
                  <p className="text-sm text-muted-foreground">{t.enableAdvisorNotesDescription}</p>
                </div>
                <Switch
                  id="advisor-notes"
                  checked={enableAdvisorNotes}
                  onCheckedChange={(checked) => {
                    setEnableAdvisorNotes(checked);
                    // QL-43 — the requirement is meaningless without the editor, and
                    // the backend clears it anyway. Mirror that here so the dialog
                    // never shows a checked "required" on a page with notes off.
                    if (!checked) setRequireAdvisorNotes(false);
                  }}
                  disabled={loading}
                />
              </div>
            )}

            {/* QL-43 — "required" is a modifier on the editor, so it only appears once
                the editor itself is enabled. Portfolio pages never render the editor.
                Keep in sync with advisorNotesAvailability (api flow-page-advisor-notes.ts). */}
            {visibility === "internal" && enableAdvisorNotes && pageType !== "portfolio" && (
              <div className="flex items-center justify-between pl-4 border-l-2">
                <div>
                  <Label htmlFor="require-advisor-notes" className="text-base">
                    {t.requireAdvisorNotes}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t.requireAdvisorNotesDescription}
                  </p>
                </div>
                <Switch
                  id="require-advisor-notes"
                  checked={requireAdvisorNotes}
                  onCheckedChange={setRequireAdvisorNotes}
                  disabled={loading}
                />
              </div>
            )}

            {/* Conditional Visibility */}
            <div className="border-t pt-4">
              <ConditionalVisibilityFields
                enabled={conditionalEnabled}
                onEnabledChange={setConditionalEnabled}
                sourceFieldId={sourceFieldId}
                onSourceFieldIdChange={setSourceFieldId}
                operator={operator}
                onOperatorChange={setOperator}
                value={conditionValue}
                onValueChange={setConditionValue}
                availableFields={availableFields}
                clientCustomFields={clientCustomFields}
                disabled={loading}
                flowLanguage={flowLanguage}
                description={t.conditionalVisibilityDescription}
                pageType={pageType}
                allowPortfolioSources={false}
                portfolioCollectionId={portfolioCollectionId}
                flowId={flowId}
              />
            </div>
          </DialogBody>

          <DialogScrollFooter>
            <DialogCancel disabled={loading}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={loading}>
              {isEditing ? t.save : t.addPage}
            </Button>
          </DialogScrollFooter>
        </form>
      </DialogScrollContent>
    </Dialog>
  );
}
